import type { ChallengeData, ChallengeObjective } from '@/models/challenges';
import { CHALLENGE_TEMPLATES } from '@/models/challenges';

export class ChallengePanel {
  private container: HTMLElement;
  private incomingEl: HTMLElement;
  private activeEl: HTMLElement;
  private sendEl: HTMLElement;

  private onAccept: (id: string) => void;
  private onDecline: (id: string) => void;
  private onSend: (targetId: string, objective: ChallengeObjective) => void;

  constructor(
    parent: HTMLElement,
    onAccept: (id: string) => void,
    onDecline: (id: string) => void,
    onSend: (targetId: string, objective: ChallengeObjective) => void,
  ) {
    this.onAccept = onAccept;
    this.onDecline = onDecline;
    this.onSend = onSend;

    this.container = document.createElement('div');
    this.container.id = 'challenge-panel';
    this.container.className = 'game-panel';
    this.container.style.borderColor = 'rgba(91, 155, 213, 0.5)';

    this.container.innerHTML = `
      <div class="game-panel-header">
        <div class="game-panel-title" style="color: var(--blue);">⚔️ CHALLENGES</div>
        <button class="game-panel-close">&times;</button>
      </div>
      <div id="challenge-incoming-section">
        <div style="font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;">Incoming</div>
        <div id="challenge-incoming"></div>
      </div>
      <div id="challenge-active-section" style="margin-top: 12px;">
        <div style="font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;">Active</div>
        <div id="challenge-active"></div>
      </div>
      <div id="challenge-send-section" style="margin-top: 12px;">
        <div style="font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;">Send Challenge</div>
        <div id="challenge-send"></div>
      </div>
    `;

    this.incomingEl = this.container.querySelector('#challenge-incoming')!;
    this.activeEl = this.container.querySelector('#challenge-active')!;
    this.sendEl = this.container.querySelector('#challenge-send')!;

    this.container.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());

    this.buildSendSection();
    parent.appendChild(this.container);
  }

  private buildSendSection(): void {
    const targetInput = document.createElement('input');
    targetInput.type = 'text';
    targetInput.placeholder = 'Player ID…';
    targetInput.style.cssText = `
      width: 100%; box-sizing: border-box; padding: 6px 8px; font-size: 12px;
      background: var(--bg-card); border: 1px solid var(--border);
      border-radius: var(--radius-sm); color: var(--text);
      font-family: var(--font); margin-bottom: 6px;
    `;
    this.sendEl.appendChild(targetInput);

    for (const tmpl of CHALLENGE_TEMPLATES) {
      const btn = document.createElement('button');
      btn.className = 'btn';
      btn.style.cssText = `
        width: 100%; padding: 8px; font-size: 11px; text-align: left;
        color: var(--blue); background: var(--bg-button);
        border-color: rgba(91, 155, 213, 0.3); margin-bottom: 4px;
      `;
      btn.innerHTML = `<span style="font-weight:700;">${tmpl.name}</span> <span style="color:var(--text-dim); font-size:10px;">— ${tmpl.description} (+${tmpl.gemsReward} gems)</span>`;
      btn.addEventListener('click', () => {
        const targetId = targetInput.value.trim();
        if (!targetId) return;
        this.onSend(targetId, tmpl.objective);
        targetInput.value = '';
      });
      this.sendEl.appendChild(btn);
    }
  }

  update(challenges: ChallengeData[]): void {
    const incoming = challenges.filter(c => c.status === 'pending');
    const active = challenges.filter(c => c.status === 'active');

    this.renderIncoming(incoming);
    this.renderActive(active);
  }

  private renderIncoming(challenges: ChallengeData[]): void {
    this.incomingEl.innerHTML = '';
    if (challenges.length === 0) {
      this.incomingEl.innerHTML = '<div style="color: var(--text-dim); font-size: 11px; text-align: center;">No incoming challenges</div>';
      return;
    }

    for (const c of challenges) {
      const card = document.createElement('div');
      card.className = 'challenge-card';

      const header = document.createElement('div');
      header.className = 'challenge-header';

      const objective = document.createElement('span');
      objective.className = 'challenge-objective';
      objective.textContent = this.objectiveLabel(c.objective);

      const timer = document.createElement('span');
      timer.className = 'challenge-timer';
      timer.textContent = this.formatExpiry(c.expiresAt);

      header.appendChild(objective);
      header.appendChild(timer);

      const vs = document.createElement('div');
      vs.className = 'challenge-vs';
      vs.textContent = `from ${c.challengerName}  •  target: ${c.targetValue}`;

      const actions = document.createElement('div');
      actions.className = 'challenge-actions';

      const acceptBtn = document.createElement('button');
      acceptBtn.className = 'btn';
      acceptBtn.textContent = 'Accept';
      acceptBtn.style.cssText = 'flex: 1; padding: 6px; font-size: 11px; color: var(--green); border-color: rgba(74, 156, 94, 0.4); background: var(--bg-button);';
      acceptBtn.addEventListener('click', () => this.onAccept(c.id));

      const declineBtn = document.createElement('button');
      declineBtn.className = 'btn';
      declineBtn.textContent = 'Decline';
      declineBtn.style.cssText = 'flex: 1; padding: 6px; font-size: 11px; color: var(--red); border-color: rgba(231, 76, 60, 0.4); background: var(--bg-button);';
      declineBtn.addEventListener('click', () => this.onDecline(c.id));

      actions.appendChild(acceptBtn);
      actions.appendChild(declineBtn);

      card.appendChild(header);
      card.appendChild(vs);
      card.appendChild(actions);
      this.incomingEl.appendChild(card);
    }
  }

  private renderActive(challenges: ChallengeData[]): void {
    this.activeEl.innerHTML = '';
    if (challenges.length === 0) {
      this.activeEl.innerHTML = '<div style="color: var(--text-dim); font-size: 11px; text-align: center;">No active challenges</div>';
      return;
    }

    for (const c of challenges) {
      const card = document.createElement('div');
      card.className = 'challenge-card';

      const header = document.createElement('div');
      header.className = 'challenge-header';

      const objective = document.createElement('span');
      objective.className = 'challenge-objective';
      objective.textContent = this.objectiveLabel(c.objective);

      const reward = document.createElement('span');
      reward.style.cssText = 'font-size: 10px; color: var(--purple);';
      reward.textContent = `+${c.gemsReward} gems`;

      header.appendChild(objective);
      header.appendChild(reward);

      const vs = document.createElement('div');
      vs.className = 'challenge-vs';
      vs.textContent = `${c.challengerName} vs ${c.challengedName}`;

      // Challenger progress bar
      const challengerLabel = document.createElement('div');
      challengerLabel.style.cssText = 'font-size: 10px; color: var(--text-dim); margin-top: 4px;';
      challengerLabel.textContent = `${c.challengerName}: ${c.challengerProgress} / ${c.targetValue}`;

      const challengerBar = this.buildProgressBar(c.challengerProgress, c.targetValue);

      // Challenged progress bar
      const challengedLabel = document.createElement('div');
      challengedLabel.style.cssText = 'font-size: 10px; color: var(--text-dim); margin-top: 4px;';
      challengedLabel.textContent = `${c.challengedName}: ${c.challengedProgress} / ${c.targetValue}`;

      const challengedBar = this.buildProgressBar(c.challengedProgress, c.targetValue);

      const timer = document.createElement('div');
      timer.className = 'challenge-timer';
      timer.style.marginTop = '4px';
      timer.textContent = `Expires: ${this.formatExpiry(c.expiresAt)}`;

      card.appendChild(header);
      card.appendChild(vs);
      card.appendChild(challengerLabel);
      card.appendChild(challengerBar);
      card.appendChild(challengedLabel);
      card.appendChild(challengedBar);
      card.appendChild(timer);

      this.activeEl.appendChild(card);
    }
  }

  private buildProgressBar(current: number, target: number): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'progress-bar';

    const fill = document.createElement('div');
    fill.className = 'progress-fill';
    const pct = Math.min(100, target > 0 ? (current / target) * 100 : 0);
    fill.style.width = `${pct}%`;

    wrap.appendChild(fill);
    return wrap;
  }

  private objectiveLabel(objective: string): string {
    const tmpl = CHALLENGE_TEMPLATES.find(t => t.objective === objective);
    return tmpl ? tmpl.name : objective;
  }

  private formatExpiry(expiresAt: string): string {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  }

  show(): void {
    this.container.style.display = 'block';
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  toggle(): void {
    if (this.container.style.display === 'none') {
      this.show();
    } else {
      this.hide();
    }
  }

  destroy(): void {
    this.container.remove();
  }
}
